import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { apiGet, apiPost } from '../services/api'
import { useAuthStore } from '../hooks/useAuth'
import {
  BookOpen, Search, Filter, Plus, Clock, Users, Star,
  ChevronRight, Play, Lock, CheckCircle, MoreVertical
} from 'lucide-react'
import toast from 'react-hot-toast'

interface Course {
  id: string
  title: string
  description: string
  instructor: string
  thumbnail: string
  duration: string
  students: number
  rating: number
  progress: number
  price: number
  category: string
  lessons: number
  enrolled: boolean
  level: 'beginner' | 'intermediate' | 'advanced'
}

export default function Courses() {
  const { user } = useAuthStore()
  const [courses, setCourses] = useState<Course[]>([])
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('all')
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)

  useEffect(() => {
    loadCourses()
  }, [])

  const loadCourses = async () => {
    try {
      const data = await apiGet('/courses')
      setCourses(data.courses || mockCourses)
    } catch {
      setCourses(mockCourses)
    } finally {
      setLoading(false)
    }
  }

  const mockCourses: Course[] = [
    {
      id: '1', title: 'Advanced Mathematics', description: 'Master calculus, linear algebra, and differential equations',
      instructor: 'Dr. Sarah Johnson', thumbnail: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400',
      duration: '48h', students: 1234, rating: 4.8, progress: 65, price: 49.99,
      category: 'math', lessons: 24, enrolled: true, level: 'advanced'
    },
    {
      id: '2', title: 'Python Programming Masterclass', description: 'From zero to hero in Python development',
      instructor: 'Alex Chen', thumbnail: 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=400',
      duration: '36h', students: 3456, rating: 4.9, progress: 30, price: 39.99,
      category: 'programming', lessons: 18, enrolled: true, level: 'beginner'
    },
    {
      id: '3', title: 'Physics Fundamentals', description: 'Understanding the laws of the universe',
      instructor: 'Prof. Michael Brown', thumbnail: 'https://images.unsplash.com/photo-1636466497213-4320c0c4c186?w=400',
      duration: '42h', students: 890, rating: 4.7, progress: 0, price: 44.99,
      category: 'science', lessons: 21, enrolled: false, level: 'intermediate'
    },
    {
      id: '4', title: 'English Literature', description: 'Classic and modern literature analysis',
      instructor: 'Emma Wilson', thumbnail: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=400',
      duration: '30h', students: 567, rating: 4.6, progress: 0, price: 29.99,
      category: 'language', lessons: 15, enrolled: false, level: 'intermediate'
    },
    {
      id: '5', title: 'Machine Learning & AI', description: 'Deep learning, neural networks, and AI applications',
      instructor: 'Dr. James Lee', thumbnail: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400',
      duration: '60h', students: 2345, rating: 4.9, progress: 0, price: 79.99,
      category: 'programming', lessons: 30, enrolled: false, level: 'advanced'
    },
    {
      id: '6', title: 'Chemistry Basics', description: 'Atomic structure, bonding, and reactions',
      instructor: 'Dr. Lisa Park', thumbnail: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=400',
      duration: '28h', students: 789, rating: 4.5, progress: 0, price: 34.99,
      category: 'science', lessons: 14, enrolled: false, level: 'beginner'
    },
  ]

  const filtered = courses.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(search.toLowerCase()) ||
                         c.description.toLowerCase().includes(search.toLowerCase())
    const matchesCategory = category === 'all' || c.category === category
    return matchesSearch && matchesCategory
  })

  const categories = ['all', 'math', 'science', 'programming', 'language']

  const handleEnroll = async (courseId: string) => {
    try {
      await apiPost(`/courses/${courseId}/enroll`, {})
      toast.success('Enrolled successfully!')
      loadCourses()
    } catch {
      toast.error('Enrollment failed')
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Courses</h1>
          <p className="text-slate-500 mt-1">Explore and manage your learning path</p>
        </div>
        {user?.role === 'admin' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Course
          </button>
        )}
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search courses..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary"
          />
        </div>
        <div className="flex gap-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                category === cat ? 'bg-primary text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Course Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3].map(i => (
            <div key={i} className="glass rounded-xl h-80 animate-pulse bg-slate-800/50" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(course => (
            <div key={course.id} className="glass rounded-xl overflow-hidden group hover:border-primary/30 transition-all">
              <div className="relative h-48 overflow-hidden">
                <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" />
                <div className="absolute top-3 right-3 flex gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    course.level === 'beginner' ? 'bg-success/20 text-success' :
                    course.level === 'intermediate' ? 'bg-warning/20 text-warning' :
                    'bg-danger/20 text-danger'
                  }`}>
                    {course.level.toUpperCase()}
                  </span>
                </div>
                {course.enrolled && (
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span>Progress</span>
                      <span>{course.progress}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${course.progress}%` }} />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-lg mb-1">{course.title}</h3>
                <p className="text-sm text-slate-500 mb-3 line-clamp-2">{course.description}</p>
                <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {course.duration}</span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {course.students}</span>
                  <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-400" /> {course.rating}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold">${course.price}</span>
                  {course.enrolled ? (
                    <Link
                      to={`/courses/${course.id}`}
                      className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark rounded-lg text-sm font-medium transition-colors"
                    >
                      <Play className="w-4 h-4" /> Continue
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleEnroll(course.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm font-medium transition-colors"
                    >
                      <Lock className="w-4 h-4" /> Enroll
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
